import { useMemo, useState } from 'react';
import { cn } from '../../../../shared';
import type { Recruiter } from '../../types/taActionBoard';
import { COLORS, initials } from './format';

interface TeamSidebarProps {
  recruiters: Recruiter[];
  myEmail: string;
  selectedEmail: string;
  onSelect: (email: string) => void;
}

/**
 * Whether the sidebar starts collapsed. COLLAPSED BY DEFAULT (product decision
 * 2026-08-10): recruiters rarely switch to a colleague's board, so the names
 * column gives its width to the board content. The expanded/collapsed choice
 * is remembered per browser.
 */
const COLLAPSE_KEY = 'ta-board-sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    // window.localStorage, never the bare global — Node 25 ships its own
    // `localStorage` that shadows the DOM one under vitest (see boardCache.ts)
    const stored = window.localStorage.getItem(COLLAPSE_KEY);
    return stored === null ? true : stored === '1';
  } catch {
    return true;
  }
}

/**
 * Left team sidebar — ONE flat alphabetical list of every recruiter. Collapsed
 * it is a narrow avatar rail (initials only, names in tooltips); expanded it
 * shows names, the "(you)" marker and out-of-office lines. Dark (FINN black) so
 * the navigation rail reads as chrome, distinct from the white board content.
 * The signed-in user is NOT pinned to the top.
 */
export function TeamSidebar({ recruiters, myEmail, selectedEmail, onSelect }: TeamSidebarProps) {
  // one alphabetical list, the signed-in user included (server order is already
  // by name, but the sidebar must not depend on that)
  const sorted = useMemo(
    () => [...recruiters].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)),
    [recruiters]
  );
  // stable "now" for OOO checks — impure calls are not allowed during render
  const [now] = useState(() => Date.now());
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = () => {
    setCollapsed((v) => {
      try {
        window.localStorage.setItem(COLLAPSE_KEY, v ? '0' : '1');
      } catch {
        /* private mode — the toggle still works for this visit */
      }
      return !v;
    });
  };

  return (
    <aside
      className={cn(
        'shrink-0 border-r border-white/10 bg-[#1A1A1A] transition-all',
        collapsed ? 'w-16 p-2' : 'w-60 p-3'
      )}
    >
      <div className={cn('flex items-center pb-2 pt-1', collapsed ? 'justify-center' : 'justify-between px-2')}>
        {!collapsed && (
          <span className="text-xs font-semibold uppercase tracking-wide text-white/40">Team</span>
        )}
        <button
          onClick={toggle}
          className="rounded-md p-1 text-white/50 hover:bg-white/10 hover:text-white/90"
          title={collapsed ? 'Show recruiter names' : 'Collapse the team list'}
          aria-label={collapsed ? 'Expand team sidebar' : 'Collapse team sidebar'}
          aria-expanded={!collapsed}
        >
          {collapsed ? '»' : '«'}
        </button>
      </div>
      <nav className="space-y-1">
        {sorted.map((r) => (
          <SidebarEntry
            key={r.email}
            label={r.name || r.email}
            isMe={r.email === myEmail}
            oooUntil={r.oooUntil}
            avatarName={r.name || r.email}
            active={selectedEmail === r.email}
            now={now}
            collapsed={collapsed}
            onClick={() => onSelect(r.email)}
          />
        ))}
      </nav>
    </aside>
  );
}

function SidebarEntry({
  label,
  isMe,
  oooUntil,
  avatarName,
  active,
  now,
  collapsed,
  onClick,
}: {
  label: string;
  /** the signed-in user's own row — subtle muted marker, no pinning */
  isMe: boolean;
  oooUntil?: string | null;
  avatarName: string;
  active: boolean;
  now: number;
  collapsed: boolean;
  onClick: () => void;
}) {
  const isOoo = !!oooUntil && new Date(oooUntil).getTime() >= now;
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors',
        collapsed && 'justify-center px-0',
        active ? 'font-semibold' : 'text-white/70 hover:bg-white/10'
      )}
      style={active ? { backgroundColor: COLORS.blueBg, color: COLORS.blueText } : undefined}
      // collapsed rail: the tooltip carries what the hidden text would have said
      title={collapsed ? `${label}${isMe ? ' (you)' : ''}${isOoo ? ' · OOO' : ''}` : undefined}
    >
      <span
        className={cn(
          'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
          active ? 'bg-white' : 'bg-white/10 text-white/70'
        )}
        style={active ? { color: COLORS.blueText } : undefined}
      >
        {initials(avatarName)}
        {collapsed && isOoo && (
          <span
            role="img"
            className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-[#1A1A1A]"
            style={{ backgroundColor: COLORS.statusOrange }}
            aria-label="Out of office"
          />
        )}
      </span>
      {!collapsed && (
        <span className="min-w-0">
          <span className="block truncate text-sm">
            {label}
            {isMe && <span className="ml-1 font-normal opacity-60">(you)</span>}
          </span>
          {isOoo && (
            <span className="block truncate text-xs opacity-80">
              OOO until{' '}
              {new Date(oooUntil as string).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </span>
      )}
    </button>
  );
}
